import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

const TONE: Record<'warning' | 'error' | 'info' | 'neutral', string> = {
  warning: 'border-warning/40 bg-warning-tint',
  error: 'border-destructive/40 bg-error-tint',
  info: 'border-[var(--info)]/40 bg-[var(--info-tint)]',
  neutral: 'border-border bg-muted',
};

const ICON_TONE: Record<keyof typeof TONE, string> = {
  warning: 'text-warning',
  error: 'text-destructive',
  info: 'text-[var(--info)]',
  neutral: 'text-muted-foreground',
};

/** A box above the page's data that says what is true and offers what to do (boards 3.2–3.5). */
export function Notice({
  tone,
  icon: Icon,
  title,
  children,
  actions,
}: {
  readonly tone: keyof typeof TONE;
  readonly icon: LucideIcon;
  readonly title: string;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn('text-foreground flex items-start gap-3 rounded-xl border p-3', TONE[tone])}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-5 shrink-0', ICON_TONE[tone])} />
      <div className="flex min-w-0 flex-col gap-1 text-sm">
        <b>{title}</b>
        {children}
        {actions ? <div className="mt-2 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
